/**
 * Level data schema, v2.0.0.
 *
 * This is one of the few things committed to *before* a second game exists,
 * because level JSON is authored data: changing it later means migrating every
 * file, not just editing code. See ADR-0010 and SDD-006-level-data-schema.md.
 *
 * The split is deliberate: everything a game does NOT need to interpret lives
 * in `Level`; everything game-specific lives in `Level.rules`, which the shell
 * treats as opaque. The shell can list, order, gate and score levels for a game
 * it knows nothing about.
 */

/** Bump on any breaking change. Loader rejects majors it does not understand. */
export const LEVEL_SCHEMA_VERSION = '2.0.0';

export type GameId = string;
export type LevelId = string;

/**
 * `'daily'` was here and was removed in Sprint 1b. It needs a server, ADR-0007
 * rules that out for v1, and nothing authored or played one — but every rule
 * written in terms of the mode still had to say whether a daily level is ordered
 * (`DATA-012`) and whether its play is bounded (`DATA-013`), so its only effect
 * was to make two answers up. Add it back with the feature. See SDD-006 open
 * question 3.
 */
export type LevelMode = 'adventure' | 'freeplay';

/** Reach a score. Shell-interpretable. */
export interface ScoreGoal {
  kind: 'score';
  target: number;
}

/** Collect N of a named piece. `piece` is a game-defined token, e.g. "gem.red". */
export interface CollectGoal {
  kind: 'collect';
  piece: string;
  count: number;
}

/** Clear N obstacles of a game-defined kind. */
export interface ClearObstacleGoal {
  kind: 'clear-obstacle';
  obstacle: string;
  count: number;
}

/**
 * Escape hatch. A game may define goals the shell cannot reason about; the
 * shell renders `label` and relies on the game to report progress.
 */
export interface CustomGoal {
  kind: 'custom';
  id: string;
  label: string;
  target: number;
}

export type Goal = ScoreGoal | CollectGoal | ClearObstacleGoal | CustomGoal;

export interface GridSize {
  cols: number;
  rows: number;
}

export interface Constraints {
  /** 0 means unlimited. */
  moveLimit: number;
  /** 0 means untimed. */
  timeLimitMs: number;
}

export interface Level {
  id: LevelId;
  gameId: GameId;
  schemaVersion: string;
  mode: LevelMode;
  title: string;
  /**
   * Ordering within the game's mode, 1-based. A mode with no ordering — a lone
   * freeplay level — uses 0. Unique within a mode, not across modes.
   */
  index: number;
  /** Author-facing hint, 1..10. Not used for gating. */
  difficulty: number;
  /** Deterministic RNG seed. Same seed + same rules => same board. */
  seed: number;
  grid: GridSize;
  constraints: Constraints;
  goals: Goal[];
  /**
   * Game-specific payload. The shell never reads inside this. Match-3 puts
   * `spawnTable` and the optional `initialBoard` here.
   */
  rules?: Record<string, unknown>;
}

/** Entry in a game's levels/index.json. Enough to render a level select
 *  without fetching every level file. */
export interface LevelIndexEntry {
  id: LevelId;
  index: number;
  title: string;
  difficulty: number;
  mode: LevelMode;
  file: string;
}

export interface LevelIndex {
  gameId: GameId;
  schemaVersion: string;
  levels: LevelIndexEntry[];
}
